import React from "react";
import { TumblingEObject, TumblingEStatus } from "types/testTypes";
import { TumblingECharList } from "@constants/TumblingEChartConstants";
import { resolveChartAccent } from "@constants/resultColors";
import { Box, Text, useMantineTheme } from "@mantine/core";
import { TumblingEViewer } from "../../../common/TumblingEViewer";
import {
  getDisplayValuesFromSnellenSize,
  getDecimalFromSnellenSize,
  getSnellenStringFromSize,
  getVisualAcuityLabelByNotation,
  VisualAcuityNotation,
} from "@utils/test-specific-utils/VisualAcuityUtils";

export interface TumblingEChartProps {
  esOfInterestIds?: TumblingEObject[];
  style?: React.CSSProperties;
  thresholdRow?: number | null;
  assumeLargerRowsCorrect?: boolean;
  notation?: VisualAcuityNotation;
  baseChart?: TumblingEObject[];
}

const getHighestPassedRow = (esOfInterestIds: TumblingEObject[]): number | null => {
  const respondedStimuliByRow: Record<number, TumblingEObject[]> = {};
  esOfInterestIds
    .filter(
      (item) =>
        item?.row !== null &&
        item?.row !== undefined &&
        item.status !== TumblingEStatus.CURRENTLY_DISPLAYED &&
        item.status !== TumblingEStatus.HIDDEN,
    )
    .forEach((item) => {
      const row = item.row as number;
      if (!respondedStimuliByRow[row]) respondedStimuliByRow[row] = [];
      respondedStimuliByRow[row].push(item);
    });

  return Object.entries(respondedStimuliByRow).reduce<number | null>((highestPassedRow, entry) => {
    const row = Number(entry[0]);
    const responses = entry[1];
    const totalStimuli = responses.length;
    const correctResponses = responses.filter(
      (response) => response.status === TumblingEStatus.CORRECTLY_RESPONDED,
    ).length;

    return correctResponses > totalStimuli / 2
      ? highestPassedRow === null
        ? row
        : Math.max(highestPassedRow, row)
      : highestPassedRow;
  }, null);
};

const TumblingEChartView: React.FC<TumblingEChartProps> = ({
  esOfInterestIds,
  style,
  thresholdRow,
  assumeLargerRowsCorrect = false,
  notation,
  baseChart,
}) => {
  const gapPx = 10;
  const cellSizePx = 15;
  const theme = useMantineTheme();
  const thresholdLabelColor = resolveChartAccent(theme, "vaThresholdRow");
  const passedRowColor = resolveChartAccent(theme, "vaPassedRow");
  const hasThresholdRow = thresholdRow !== null && thresholdRow !== undefined;
  const highestPassedRow = assumeLargerRowsCorrect
    ? getHighestPassedRow(esOfInterestIds || [])
    : null;

  const snapshotMap = new Map<string, TumblingEObject>();
  (esOfInterestIds || []).forEach((s) => s?._id && snapshotMap.set(s._id, s));

  const chartItems = baseChart?.length ? baseChart : TumblingECharList;

  const mergedItems: TumblingEObject[] = (chartItems || []).map((base) => {
    const live = snapshotMap.get(base._id);
    return live
      ? {
          ...base,
          status: live.status ?? base.status,
          orientation: live.orientation ?? base.orientation,
        }
      : base;
  });

  const itemsByRow: Record<number, TumblingEObject[]> = {};
  (mergedItems || []).forEach((i) => {
    const r = i.row ?? 0;
    if (!itemsByRow[r]) itemsByRow[r] = [];
    itemsByRow[r].push(i);
  });

  const sortedRowKeys = Object.keys(itemsByRow)
    .map((k) => Number(k))
    .sort((a, b) => a - b);

  const rowNodes = sortedRowKeys.map((rowKey) => {
    const rowItems = itemsByRow[rowKey]
      .slice()
      .sort((a, b) => ((a.column as number) ?? 0) - ((b.column as number) ?? 0));

    const colsCount = Math.max(rowItems.length, 1);
    const snellenSize = rowItems[0]?.snellenSize;
    const displayValues = getDisplayValuesFromSnellenSize(snellenSize);
    const snellenLabel = getSnellenStringFromSize(snellenSize) ?? "";
    const rightLabel = notation
      ? getVisualAcuityLabelByNotation(displayValues, notation)
      : (getDecimalFromSnellenSize(snellenSize) ?? "");
    const isAssumedCorrectRow = highestPassedRow !== null && rowKey < highestPassedRow;
    const isThresholdResultRow = hasThresholdRow && rowKey === thresholdRow;
    const labelColor = isAssumedCorrectRow
      ? passedRowColor
      : isThresholdResultRow
        ? thresholdLabelColor
        : undefined;

    return (
      <Box
        key={`row-${rowKey}`}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: gapPx,
          width: "100%",
          borderRadius: isThresholdResultRow ? 8 : undefined,
          backgroundColor: isThresholdResultRow ? "var(--oculera-tint-blue)" : undefined,
        }}
      >
        <Text
          style={{
            justifySelf: "start",
            marginRight: gapPx * 5,
            color: labelColor,
            fontWeight: isThresholdResultRow ? 700 : 400,
          }}
        >
          {snellenLabel}
        </Text>
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${colsCount}, auto)`,
            justifyContent: "center",
            alignItems: "center",
            gap: gapPx,
          }}
        >
          {rowItems.map((item) => (
            <Box key={item._id}>
              <TumblingEViewer
                tumblingE={item}
                baseSizePx={cellSizePx}
                colorOverride={isAssumedCorrectRow ? passedRowColor : undefined}
              />
            </Box>
          ))}
        </Box>
        <Text
          style={{
            justifySelf: "end",
            marginLeft: gapPx * 5,
            color: labelColor,
            fontWeight: isThresholdResultRow ? 700 : 600,
          }}
        >
          {rightLabel}
        </Text>
      </Box>
    );
  });

  return (
    <Box
      style={{
        display: "grid",
        gap: gapPx * 2,
        justifyContent: "center",
        alignContent: "center",
        justifyItems: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        ...(style || {}),
      }}
    >
      {rowNodes}
    </Box>
  );
};

export default TumblingEChartView;
